import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

export default function AttendanceStats({ branchId }) {
  useAuth();
  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const branchParam = branchId ? { branch_id: branchId } : {};
      const [membersRes, attendRes] = await Promise.all([
        api.get('/members', { params: { status: 'active', limit: 2000, ...branchParam } }),
        api.get('/attendances/today', { params: { ...branchParam } }),
      ]);

      const totalActive = (membersRes?.data || [])?.length;
      // Unique members who checked in today; filter client-side by branch when needed
      const attendances = (attendRes?.data || [])?.filter(a => !branchId || a?.branch_id === branchId);
      const checkedInIds = new Set(attendances?.map(a => a?.member_id));
      const present = checkedInIds?.size;
      const absent = Math.max(0, totalActive - present);

      setStats({ present, absent, total: totalActive });
    } catch (err) {
      console.error("Error fetching attendance stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [branchId]);

  const todayLabel = new Date()?.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const attendanceRate = stats?.total > 0 ? Math.round((stats?.present / stats?.total) * 100) : 0;

  return (
    <div className="bg-[var(--color-card)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center">
            <Icon name="CalendarCheck" size={15} color="#2563eb" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Today's Attendance</h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{todayLabel}</p>
          </div>
        </div>
        <button
          onClick={fetchStats}
          className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={13} color="var(--color-muted-foreground)" />
        </button>
      </div>
      {loading ? (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-20 bg-[var(--color-muted)] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Present */}
            <div className="flex flex-col items-center justify-center p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mb-1.5">
                <Icon name="UserCheck" size={15} color="#16a34a" />
              </div>
              <p className="text-xl font-bold text-green-700 font-[var(--font-heading)]">{stats?.present}</p>
              <p className="text-xs text-green-600 font-[var(--font-caption)]">Present</p>
            </div>

            {/* Absent */}
            <div className="flex flex-col items-center justify-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mb-1.5">
                <Icon name="UserX" size={15} color="#dc2626" />
              </div>
              <p className="text-xl font-bold text-red-600 font-[var(--font-heading)]">{stats?.absent}</p>
              <p className="text-xs text-red-500 font-[var(--font-caption)]">Absent</p>
            </div>

            {/* Total */}
            <div className="flex flex-col items-center justify-center p-3 bg-[var(--color-muted)]/40 border border-[var(--color-border)] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-1.5">
                <Icon name="Users" size={15} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{stats?.total}</p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Total Active</p>
            </div>
          </div>

          {/* Attendance Rate Bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Attendance Rate</span>
              <span className="text-xs font-bold text-[var(--color-foreground)]">{attendanceRate}%</span>
            </div>
            <div className="w-full h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${attendanceRate}%`,
                  backgroundColor: attendanceRate >= 70 ? "#16a34a" : attendanceRate >= 40 ? "#d97706" : "#dc2626",
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
